import FontAwesomeIcon from 'src/components/Icon';
import Avatar from 'src/components/Avatar';
import Tenant from 'src/components/Tenant';
import Tooltip from 'src/components/Tooltip/Tooltip';
import { ITableColumn } from 'src/components/Table/types';
import { Link } from 'react-router-dom';
import { Hidden } from 'src/components/Table';
import Button from 'src/components/Button';
import { SuiteUserRow } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/types';

const sortByString = (a: string, b: string): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.toLowerCase().localeCompare(b.toLowerCase());
};

interface CreateSuiteUsersTableColumnsParams {
  user: any;
  handleSetContactFormData: (contactId: string) => void;
  PATHS: any;
  ICONS: any;
  onRequestPhoneCall: (phone: string) => any;
  suite: any;
  property: any;
}

export const createSuiteUsersTableColumns = ({
  user,
  handleSetContactFormData,
  PATHS,
  ICONS,
  onRequestPhoneCall,
  suite,
  property,
}: CreateSuiteUsersTableColumnsParams): ITableColumn[] => {
  const userRoleName: string = user?.roles?.[0]?.role?.name;

  const canNavigateToContact: boolean =
    userRoleName === 'Admin' || userRoleName === 'Property Developer';

  const canEditContact: boolean =
    userRoleName === 'Admin' ||
    userRoleName === 'Super Admin' ||
    userRoleName === 'Property Developer';

  return [
    {
      label: 'Contact',
      sortable: true,
      accessor: 'firstname',
      minWidth: '22rem',
      cell: (data: SuiteUserRow) => (
        <Link
          className={
            canNavigateToContact
              ? 'suite-users-contact-link'
              : 'suite-users-contact-link suite-users-contact-link-disabled'
          }
          to={
            canNavigateToContact
              ? {
                  pathname: PATHS.CONTACTS_VIEW.replace(':id', data.id),
                  state: {
                    data,
                    navOrigin: {
                      kind: 'suite',
                      propertyId:
                        property?.propertyId || property?.id || suite?.propertyId,
                      suiteId: suite?.id,
                      suiteNumber: suite?.number || suite?.name,
                    },
                  },
                }
              : {}
          }
        >
          <Tenant
            headline={`${data.firstname} ${data.lastname}`}
            avatar={
              <Avatar badge={ICONS[data.roleName]} url={data.profileImage} />
            }
          />
        </Link>
      ),
    },
    {
      xsHide: true,
      label: 'Role',
      accessor: 'roleName',
      sortable: true,
      cell: (data: SuiteUserRow) => (
        <div className="suite-users-role-cell">
          <div>{data.roleName}</div>
          {data.contactType ? (
            <div className="suite-users-role-contact-type">
              {'- ' + data.contactType}
            </div>
          ) : null}
          {data.isPrimaryContact === true ? (
            <div className="suite-users-role-primary-icon">
              <Tooltip content="This Home Owner is the primary contact for this suite — the main point of contact for unit communications.">
                <FontAwesomeIcon glyph="sun" weight="regular" size="small" />
              </Tooltip>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      label: 'Phone',
      accessor: 'phone',
      cell: (data: SuiteUserRow) => onRequestPhoneCall(data.phone),
    },
    {
      xsHide: true,
      label: 'Email',
      accessor: 'email',
    },
    {
      xsHide: true,
      label: 'Last login',
      accessor: 'lastLoggedOn',
      sortable: true,
      cell: (data: SuiteUserRow) => data.lastLoggedOnFormatted,
    },
    {
      xsHide: true,
      sortable: true,
      label: 'Status',
      width: '8rem',
      accessor: 'status',
      sortingAlgorithm: sortByString,
      cell: (data: SuiteUserRow) => (
        <div>
          <div title={data.status}>{data.status}</div>
        </div>
      ),
    },
    {
      cell: (data: SuiteUserRow) => (
        <Hidden>
          {canEditContact ? (
            <Button
              color="semantic"
              label={<FontAwesomeIcon glyph="pencil" weight="light" />}
              onClick={() => handleSetContactFormData(data.id)}
            />
          ) : null}
        </Hidden>
      ),
    },
  ];
};
